// The ONE top-right action in the roster shell's per-artist context header.
//
// The per-artist pages stopped rendering headers of their own (PO, 2026-09-02) — the shell's context header
// is the one header — but each lens's dashboard still owns an action there: Add task (Management), Refresh
// (Label), New offer (Booking). The shell cannot derive those: it would have to know each lens's queries,
// its modals and its routes, which is precisely the coupling one header was meant to remove.
//
// So the PAGE registers and the SHELL renders, through a one-field store — the `teamPanel.ts` / `settingsPanel.ts`
// shape, for the same reason: UI state settable from any depth, no prop-threading through a router Outlet and
// no second context provider.
//
// Registration is tied to the page's LIFETIME, so the action leaves with it. Switching from the dashboard to
// the Music tab unmounts the registrar and the header goes back to having no action — which is correct, and
// is why this is not driven off (subview × tab) in the shell: the page that owns the action is the only
// thing that reliably knows whether it is on screen.
import { useEffect, useRef } from "react";
import type { LucideIcon } from "lucide-react";
import { create } from "zustand";
import { registerStoreReset } from "./stores.js";

/** The visual weight of a header action. Omitted ⇒ the Button's default (primary). "ghost" is the white
 *  outline the Booking "New offer" action uses, to match its card button and the all-roster page's. */
export type RosterHeaderVariant = "ghost";

export interface RosterHeaderAction {
  label: string;
  onClick: () => void;
  /** A leading icon (Booking's "New offer" carries the same Plus its card button does). */
  icon?: LucideIcon;
  variant?: RosterHeaderVariant;
  /** Button size; omitted ⇒ the default. Booking uses "sm" to match its ghost card button. */
  size?: "sm";
}

interface RosterHeaderActionState {
  action: RosterHeaderAction | null;
  set: (action: RosterHeaderAction) => void;
  clear: () => void;
  reset: () => void;
}

export const useRosterHeaderActionStore = create<RosterHeaderActionState>((set) => ({
  action: null,
  set: (action) => set({ action }),
  clear: () => set({ action: null }),
  reset: () => set({ action: null }),
}));

// Scorch on org/band switch + logout: an action registered by the previous tenant's page must never fire
// over the new one (stores.ts).
registerStoreReset(() => useRosterHeaderActionStore.getState().reset());

/**
 * Register this page's header action for as long as it is mounted.
 *
 * The handler rides a REF so a page whose callback closes over changing state (a refetch, a modal setter)
 * does not re-register on every render — the effect keys on the LABEL alone, and the ref keeps the closure
 * current. Registering per render would thrash the store and, through it, the shell.
 *
 * Pass `null` for a page that wants no action; the effect then does nothing and clears nothing, so a page
 * cannot silently steal the slot from a sibling that legitimately holds it.
 */
export function useRosterHeaderAction(
  label: string | null,
  onClick: () => void,
  opts?: { icon?: LucideIcon; variant?: RosterHeaderVariant; size?: "sm" },
): void {
  const set = useRosterHeaderActionStore((s) => s.set);
  const clear = useRosterHeaderActionStore((s) => s.clear);
  const handler = useRef(onClick);
  handler.current = onClick;
  // Stable per page (a module-level icon component and string literals), so they can key the effect without
  // thrashing it — no need to memoize a caller's inline object.
  const icon = opts?.icon;
  const variant = opts?.variant;
  const size = opts?.size;

  useEffect(() => {
    if (!label) return;
    const action: RosterHeaderAction = { label, onClick: () => handler.current(), icon, variant, size };
    set(action);

    // WHILE THIS PAGE IS MOUNTED IT OWNS THE SLOT — including across a scorch that empties it underneath.
    //
    // `resetAllStores()` (switchRosterArtist, switchBand, logout, the dev fixture switch) nulls this store,
    // and a roster ARTIST switch calls it without this page unmounting: /roster/artists/$artistId keeps the
    // same component instance across a param change, and RosterBandProvider only swaps in its spinner —
    // the thing that would unmount and remount the page — when the new artist's navigation document is a
    // cache MISS. That document is keyed `['me','navigation',…]`, which the switch's `['band', oldId, …]`
    // eviction deliberately does not reach, so the SECOND visit to any artist renders straight through from
    // cache. The effect above does not re-run (`label` is unchanged and both store fns are stable), so the
    // slot the scorch emptied stayed empty and the action vanished for the rest of the session — Label's
    // Refresh, Management's Add task and Booking's New offer alike (found in dev, 2026-09-02).
    //
    // Re-asserting is safe for the case the scorch is protecting against — a PREVIOUS tenant's action firing
    // over the new one — because `handler` is a ref this page rewrites on every render, so a registration
    // that survives the switch carries the NEW artist's closure. An action whose page is genuinely gone is
    // still cleared, by the unmount below.
    const unsubscribe = useRosterHeaderActionStore.subscribe((s) => {
      if (s.action === null) useRosterHeaderActionStore.getState().set(action);
    });

    // Unsubscribe BEFORE clearing, or the listener re-registers the action this cleanup is retiring.
    return () => {
      unsubscribe();
      clear();
    };
  }, [label, icon, variant, size, set, clear]);
}
